import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnvService } from '@hub/services/core/env-service';
import { Close, Settings } from '@mui/icons-material';
import { Box, Collapse, IconButton } from '@mui/material';
import { DashboardError } from '@monorepo/nova-ui';
import { useDashboard } from '../../../contexts';
import { isAccountTerminated } from '../../../utils/status-helpers';
import { ProgressOverviewLoading } from './';
import { CustomProgressBar } from './components/custom-progress-bar';
import { DebugPanel } from './components/custom-progress-bar/components/debug-panel.component';
import { useProgressDebug } from './components/custom-progress-bar/hooks/use-progress-debug.hook';
import {
  calculateProgressPercentage,
  findActiveStepIndex,
} from './components/custom-progress-bar/utils/progress-calculations.utils';
import { useProgramProgression } from './hooks/use-program-progression';

export const ProgressOverview: FC = () => {
  const { t } = useTranslation('progressOverview');

  // Get data from dashboard context instead of props
  const { account, accountBalance } = useDashboard();

  // Debug state for temporarily changing active step
  const [debugActiveStepId, setDebugActiveStepId] = useState<string | null>(
    null,
  );

  // Debug panel visibility state
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);

  // Check if we should show debug controls in non-production
  const shouldShowDebugControls = !EnvService.isProduction;

  const programId = account?.programId || '';

  const {
    data: progressionData,
    groupInfo,
    isLoading,
    isError,
  } = useProgramProgression(programId);

  // Debug hook for the debug panel - must be called before early returns
  const debug = useProgressDebug(
    accountBalance?.profitAndLoss || 0,
    account ? isAccountTerminated(account) : false,
  );

  // All hooks must be called before early returns
  // Modify progression data for debug purposes
  const debugProgressionData = useMemo(() => {
    if (!progressionData || progressionData.length === 0) {
      return [];
    }
    return debugActiveStepId
      ? progressionData.map((step) => ({
          ...step,
          active: step._id === debugActiveStepId,
        }))
      : progressionData;
  }, [progressionData, debugActiveStepId]);

  // Use debug PNL if debug panel is open, otherwise use actual PNL
  const effectiveUserProgress = useMemo(() => {
    return isDebugPanelOpen
      ? debug.actualPNL // Use actualPNL instead of debugPNL
      : accountBalance?.profitAndLoss || 0;
  }, [isDebugPanelOpen, debug.actualPNL, accountBalance?.profitAndLoss]);

  const effectiveHasError = useMemo(() => {
    return isDebugPanelOpen
      ? debug.actualError
      : account
        ? isAccountTerminated(account)
        : false;
  }, [isDebugPanelOpen, debug.actualError, account]);

  // Calculate debug panel values reactively
  const debugActiveStepIndex = useMemo(() => {
    return findActiveStepIndex(debugProgressionData);
  }, [debugProgressionData]);

  const debugProgressPercentage = useMemo(() => {
    return calculateProgressPercentage(
      debugProgressionData,
      debugActiveStepIndex,
      debug.actualPNL, // Use actualPNL instead of debugPNL
    );
  }, [debugProgressionData, debugActiveStepIndex, debug.actualPNL]);

  // Debug function to temporarily change active step
  const handleDebugStepClick = (stepId: string) => {
    setDebugActiveStepId(stepId);
  };

  // Early returns after all hooks are called
  if (!account) {
    return <ProgressOverviewLoading />;
  }

  if (isLoading) {
    return <ProgressOverviewLoading />;
  }

  if (isError) {
    return (
      <DashboardError
        title={t('error.title')}
        message={t('error.message')}
        onRetry={() => window.location.reload()} // Simple retry by refreshing
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <CustomProgressBar
        steps={debugProgressionData}
        currentUserProgress={effectiveUserProgress}
        onDebugStepClick={handleDebugStepClick}
        showDebug={false}
        hasError={effectiveHasError}
        groupInfo={groupInfo}
      />

      {/* Debug Toggle Button - Position near progress bar */}
      {shouldShowDebugControls && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={() => setIsDebugPanelOpen(!isDebugPanelOpen)}
            size="small"
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              width: 32,
              height: 32,
            })}
          >
            {isDebugPanelOpen ? (
              <Close sx={{ fontSize: 16 }} />
            ) : (
              <Settings sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Box>
      )}

      {/* Debug Panel - Show in same place as before with animation */}
      {shouldShowDebugControls && (
        <Collapse in={isDebugPanelOpen} timeout={300}>
          <DebugPanel
            steps={debugProgressionData}
            activeStepIndex={debugActiveStepIndex}
            progressPercentage={debugProgressPercentage}
            currentUserProgress={accountBalance?.profitAndLoss || 0}
            hasError={isAccountTerminated(account)}
            debug={debug}
            onDebugStepClick={handleDebugStepClick}
            groupInfo={groupInfo}
          />
        </Collapse>
      )}
    </Box>
  );
};
